import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { motion, AnimatePresence } from 'motion/react';
import { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { ArrowLeft2Icon } from '../icons/arrows/arrow-left2-icon';
import { ArrowRight2Icon } from '../icons/arrows/arrow-right2-icon';
import { SliderButton } from './slider-button';
const slideVariants = {
    hidden: (direction) => ({
        x: direction > 0 ? '100%' : '-100%',
        opacity: 0,
    }),
    visible: {
        x: 0,
        opacity: 1,
        transition: {
            duration: 0.4,
        },
    },
    exit: (direction) => ({
        x: direction > 0 ? '-100%' : '100%',
        opacity: 0,
        transition: {
            duration: 0.4,
        },
    }),
};
export const Slider = ({ indicatorVariant = 'solid', children, contentHeight, mobileContentHeight, device, withButtons = true, ref, ...props }) => {
    const [index, setIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const onPrevious = () => {
        setDirection(-1);
        setIndex((index) => index - 1);
    };
    const onNext = () => {
        setDirection(1);
        setIndex((index) => index + 1);
    };
    const onIndexSelect = (newIndex) => {
        setDirection(newIndex > index ? 1 : -1);
        setIndex(newIndex);
    };
    const hasPaginated = useRef(false);
    const detectPaginationGesture = (event, { offset }) => {
        if (hasPaginated.current) {
            return;
        }
        let newPage = index;
        const threshold = 50;
        if (offset.x < -threshold) {
            // If we're dragging left, go forward a page
            newPage = index + 1;
        }
        else if (offset.x > threshold) {
            // If we're dragging right, go backwards a page
            newPage = index - 1;
        }
        if (newPage === children.length || newPage === -1 || newPage === index) {
            return;
        }
        hasPaginated.current = true;
        // Wrap the page index to within the permitted page range
        newPage =
            newPage <= 0
                ? 0
                : newPage >= children.length - 1
                    ? children.length - 1
                    : newPage;
        onIndexSelect(newPage);
    };
    const Indicator = indicatorVariant === 'solid' ? IndicatorSolid : IndicatorSecondary;
    return (_jsxs(Root, { ref: ref, ...props, children: [_jsxs(Content, { children: [withButtons && (_jsx(ButtonVerticalStyled, { icon: _jsx(ArrowLeft2Icon, { size: 20 }), onClick: onPrevious, disabled: index === 0 })), _jsx(ChildWrapper, { "$height": contentHeight, "$mobileHeight": mobileContentHeight, children: _jsx(AnimatePresence, { custom: direction, initial: false, children: _jsx(AnimatedContent
                            // TODO: may glitch and freeze after repeated back-and-forth use.
                            // see https://github.com/motiondivision/motion/issues/2554#issuecomment-2225113484
                            // key={`${index}-${Math.random()}`}
                            // eslint-disable-next-line react-hooks/purity
                            , { custom: direction, variants: slideVariants, initial: "hidden", animate: "visible", exit: "exit", drag: device === 'mobile' ? 'x' : undefined, onDrag: detectPaginationGesture, onDragStart: () => (hasPaginated.current = false), onDragEnd: () => (hasPaginated.current = true), dragConstraints: { left: 0, right: 0, top: 0, bottom: 0 }, children: children[index] }, index === 0 ? index : `${index}-${Math.random()}`) }) }), withButtons && (_jsx(ButtonVerticalStyled, { icon: _jsx(ArrowRight2Icon, { size: 20 }), onClick: onNext, disabled: index === children.length - 1 }))] }), _jsx(Indicators, { children: children.map((_, current) => (_jsx(Indicator, { "$active": current === index, onClick: () => onIndexSelect(current) }, current))) })] }));
};
const Root = styled.div `
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
`;
const ButtonVerticalStyled = styled(SliderButton) `
  z-index: 1;
  @media (${({ theme }) => theme.mobile.queryMaxWidth}) {
    display: none;
  }
`;
const Content = styled.div `
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
`;
const ChildWrapper = styled.div `
  flex: 1;
  min-width: 0;
  position: relative;
  height: ${({ $height }) => $height}px;

  @media (${({ theme }) => theme.mobile.queryMaxWidth}) {
    ${({ $mobileHeight }) => $mobileHeight &&
    css `
        height: ${$mobileHeight}px;
      `}
  }
`;
const Indicators = styled.div `
  display: flex;
  gap: 6px;
  margin-top: 17px;

  @media (${({ theme }) => theme.mobile.queryMaxWidth}) {
    margin-top: 9px;
  }
`;
const IndicatorSolid = styled.div `
  background-color: ${({ $active, theme }) => $active ? theme.color.white[100] : theme.color.white[25]};

  width: 5px;
  height: 5px;
  border-radius: 50%;
  cursor: pointer;
`;
const IndicatorSecondary = styled(IndicatorSolid) `
  background: ${({ $active, theme }) => $active ? theme.color.white[100] : 'transparent'};
  box-shadow: inset 0px 0px 0px 1px
    ${({ $active }) => ($active ? 'transparent' : '#ffffff26')};

  width: 8px;
  height: 8px;
`;
const AnimatedContent = styled(motion.div) `
  width: 100%;
  top: 0;
  left: 0;
  position: absolute;
`;
//# sourceMappingURL=slider.js.map